import { create } from "zustand";
import { captureException } from "@sentry/react";
import { ChatMessage } from "../ChatBox/types";

import { ChatToolType, ChatEffort, SiriusMcpCategory } from "_graphql-types-frontend/graphql";

/**
 * Lifecycle of a single chat tab.
 *
 *   pending → generating → done
 *
 * - `pending`: dispatchMessage fired, no convId returned from server yet.
 *   (Also the initial state of a brand-new empty slot; the UI treats it
 *   the same as a freshly-sent chat.)
 * - `generating`: convId is known; subscription is streaming the response.
 * - `done`: subscription emitted "stop", the chat call resolved, the user
 *   cancelled, or the call errored. Either way, the slot is no longer active.
 */
export type SlotStatus = "pending" | "generating" | "done";

/**
 * A single chat tab. Each new chat creates a new slot. Switching
 * conversations changes `activeId`; slots are preserved so the user can
 * return to any previous chat with its history, draft, and queue intact.
 *
 * Keyed by `sessionId` (generated client-side, used for filtering the
 * subscription stream) in the slots map.
 */
export interface Slot {
  sessionId: string;
  conversationId: number | null;
  chatHistory: ChatMessage[];
  userText: string;
  queuedMessage: string | null;
  shouldStop: boolean;
  status: SlotStatus;
  isCompressing?: boolean;
  // How many times context was compacted during the CURRENT turn. Unlike
  // isCompressing (transient — cleared by the next stream event) this
  // accumulates, so a turn that keeps compacting is visible as it happens.
  // Reset on the turn's start and stop events.
  compressionCount?: number;
  // Transient: within-turn tool-result eviction ("freeing memory") is in
  // progress. Set on the contextEditing stream event, cleared by the next
  // event — mirrors isCompressing.
  isEditingContext?: boolean;
  // Transient: the server is retrying a model call Bedrock throttled or briefly
  // failed; its own words, for the status line. Set on the retrying stream
  // event, cleared by the next content event or the turn's start/stop.
  retryNote?: string;
  // Whether the current turn has received any stream event. Reset when a
  // message is sent (dispatchMessage), set by SlotWatcher. No event means the
  // request never reached the agent; an event means the turn is running even
  // if the HTTP response is later lost.
  streamStarted?: boolean;
  // When the current turn's last stream event arrived (ms). A running turn is
  // kept waiting only while events (heartbeats included) keep coming.
  lastStreamEventAt?: number;
  // How often the server pings a running turn's stream (ms), as its heartbeat
  // says. dispatchMessage gives the turn up after a few missed pings.
  heartbeatMs?: number;
  // The message whose turn the slot is running (dispatchMessage). A watchdog
  // belongs to one message and stands down once another has been sent.
  messageId?: string;
  createdAt: number;
}

interface ChatState {
  // === Per-conversation state ===
  slots: Map<string, Slot>;
  activeId: string;

  // === Global (truly cross-conversation) ===
  disabledTools?: ChatToolType[];
  showTools: boolean;
  menuOpen: boolean;
  menuActiveTab: string;
  knowledgeBaseId: number | null;
  /** An `llmModels` key; null = the server default. */
  model: string | null;
  /** null = no explicit choice; the model runs at its own API default. */
  effort: ChatEffort | null;
  /** Sirius MCP categories switched off in the tools panel. An exclusion list,
   *  like disabledTools, so a category added later is on by default. */
  excludedMcpCategories: SiriusMcpCategory[];
  completedConversations: Set<number>;
  dismissedPlanIds: Set<number>;
  autoSendMessage: string | null;
  /**
   * A conversation something outside the chat asked us to open — today the
   * notification bell. One-shot, like autoSendMessage: the chat panel consumes
   * it and loads it through useLoadConversation, the same path the history tab
   * uses, so history, mutation plans, artifacts and slot reuse stay in one
   * place.
   */
  pendingConversationId: number | null;

  // === Slot management ===
  /** Create a new slot, set it active, return its sessionId. */
  newChat: () => string;
  /** Set the active slot by sessionId. No-op if the slot doesn't exist. */
  switchToSlot: (sessionId: string) => void;
  /** Find a slot by convId and switch to it. Returns true if found. */
  switchToConversation: (conversationId: number) => boolean;
  /** Look up a slot by conversationId. */
  getSlotByConversationId: (conversationId: number) => Slot | undefined;

  // === Slot mutations ===
  /** Generic per-field patch on a specific slot. */
  patchSlot: (sessionId: string, patch: Partial<Slot>) => void;
  /** Generic per-field patch on the currently-active slot. */
  patchActiveSlot: (patch: Partial<Slot>) => void;
  /** Append a single message to a slot's chat history. */
  appendToSlotChatHistory: (sessionId: string, message: ChatMessage) => void;
  /** Append a single message to the active slot's chat history. */
  appendToActiveChatHistory: (message: ChatMessage) => void;
  /** Functional or replace update of a slot's chat history. */
  setSlotChatHistory: (
    sessionId: string,
    messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])
  ) => void;
  /** Functional or replace update of the active slot's chat history. */
  setActiveChatHistory: (
    messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])
  ) => void;
  /** Push the slot into the next lifecycle state. */
  setSlotStatus: (sessionId: string, status: SlotStatus) => void;

  // === Queue mutations (operate on the active slot) ===
  appendToQueuedMessage: (message: string) => void;
  consumeQueuedMessage: () => string | null;
  editQueuedMessage: () => string | null;
  clearQueuedMessage: () => void;

  // === Global actions ===
  setDisabledTools: (tools?: ChatToolType[]) => void;
  setShowTools: (show: boolean) => void;
  setMenuOpen: (open: boolean) => void;
  setMenuActiveTab: (tab: string) => void;
  setKnowledgeBaseId: (id: number | null) => void;
  setModel: (model: string) => void;
  setEffort: (effort: ChatEffort) => void;
  setExcludedMcpCategories: (keys: SiriusMcpCategory[]) => void;
  markConversationCompleted: (conversationId: number) => void;
  dismissPlan: (planId: number) => void;
  undismissPlan: (planId: number) => void;
  setAutoSendMessage: (message: string | null) => void;
  consumeAutoSendMessage: () => string | null;
  /** Ask the chat to open a conversation. The chat panel acts on it. */
  openConversation: (conversationId: number) => void;
  consumePendingConversationId: () => number | null;
}

// =============================================================================
// Selectors — import these in components to read per-slot fields.
// Each selector picks one field off the active slot, so re-renders only fire
// when that specific field changes.
// =============================================================================

export const selectActiveSlot = (s: ChatState) => s.slots.get(s.activeId);
export const selectSessionId = (s: ChatState) => s.slots.get(s.activeId)?.sessionId ?? "";
export const selectConversationId = (s: ChatState) => s.slots.get(s.activeId)?.conversationId ?? null;
export const selectChatHistory = (s: ChatState) => s.slots.get(s.activeId)?.chatHistory ?? EMPTY_HISTORY;
export const selectUserText = (s: ChatState) => s.slots.get(s.activeId)?.userText ?? "";
export const selectQueuedMessage = (s: ChatState) => s.slots.get(s.activeId)?.queuedMessage ?? null;
export const selectShouldStop = (s: ChatState) => s.slots.get(s.activeId)?.shouldStop ?? false;
export const selectActiveStatus = (s: ChatState): SlotStatus => s.slots.get(s.activeId)?.status ?? "done";
export const selectIsWaitingForResponse = (s: ChatState) => {
  const status = s.slots.get(s.activeId)?.status;
  return status === "pending" || status === "generating";
};

const EMPTY_HISTORY: ChatMessage[] = [];

// =============================================================================
// Storage helpers (preferences persist; per-slot state is in-memory)
// =============================================================================

const MODEL_KEY = "ranger-chat-model";
const EFFORT_KEY = "ranger-chat-effort";
const EXCLUDED_MCP_CATEGORIES_KEY = "ranger-chat-excluded-mcp-categories";
const DISABLED_TOOLS_KEY = "ranger-chat-disabled-tools";
const MIGRATION_V1_KEY = "ranger-chat-disabled-tools-v1";
const MIGRATION_V2_KEY = "ranger-chat-disabled-tools-v2";
const MIGRATION_V3_KEY = "ranger-chat-disabled-tools-v3";
const MIGRATION_V4_KEY = "ranger-chat-disabled-tools-v4";
const MIGRATION_V5_KEY = "ranger-chat-disabled-tools-v5";

const getSessionId = () =>
  `${Date.now().toString(36)}::${Math.random().toString(36).slice(2)}`;

function migrateDisabledToolsV1(): void {
  try {
    if (localStorage.getItem(MIGRATION_V1_KEY)) return;
    const stored = localStorage.getItem(DISABLED_TOOLS_KEY);
    const current: string[] = stored ? JSON.parse(stored) : [];
    if (!current.includes(ChatToolType.AlphaVantageMcp)) {
      current.push(ChatToolType.AlphaVantageMcp);
      localStorage.setItem(DISABLED_TOOLS_KEY, JSON.stringify(current));
    }
    localStorage.setItem(MIGRATION_V1_KEY, "1");
  } catch (e) {
    captureException(e);
  }
}

function migrateDisabledToolsV2(): void {
  try {
    if (localStorage.getItem(MIGRATION_V2_KEY)) return;
    localStorage.setItem(MIGRATION_V2_KEY, "1");
  } catch (e) {
    captureException(e);
  }
}

function migrateDisabledToolsV3(): void {
  try {
    if (localStorage.getItem(MIGRATION_V3_KEY)) return;
    const stored = localStorage.getItem(DISABLED_TOOLS_KEY);
    const current: string[] = stored ? JSON.parse(stored) : [];
    const filtered = current.filter(t => t !== ChatToolType.AtlasTool);
    localStorage.setItem(DISABLED_TOOLS_KEY, JSON.stringify(filtered));
    localStorage.setItem(MIGRATION_V3_KEY, "1");
  } catch (e) {
    captureException(e);
  }
}

function migrateDisabledToolsV4(): void {
  try {
    if (localStorage.getItem(MIGRATION_V4_KEY)) return;
    const stored = localStorage.getItem(DISABLED_TOOLS_KEY);
    const current: string[] = stored ? JSON.parse(stored) : [];
    const toAdd = [ChatToolType.StageMutationsTool, ChatToolType.ModifyMutationPlanTool];
    for (const tool of toAdd) {
      if (!current.includes(tool)) current.push(tool);
    }
    localStorage.setItem(DISABLED_TOOLS_KEY, JSON.stringify(current));
    localStorage.setItem(MIGRATION_V4_KEY, "1");
  } catch (e) {
    captureException(e);
  }
}

function migrateDisabledToolsV5(): void {
  try {
    if (localStorage.getItem(MIGRATION_V5_KEY)) return;
    const stored = localStorage.getItem(DISABLED_TOOLS_KEY);
    const current: string[] = stored ? JSON.parse(stored) : [];
    if (!current.includes(ChatToolType.SpawnSubAgentTool)) {
      current.push(ChatToolType.SpawnSubAgentTool);
    }
    localStorage.setItem(DISABLED_TOOLS_KEY, JSON.stringify(current));
    localStorage.setItem(MIGRATION_V5_KEY, "1");
  } catch (e) {
    captureException(e);
  }
}

migrateDisabledToolsV1();
migrateDisabledToolsV2();
migrateDisabledToolsV3();
migrateDisabledToolsV4();
migrateDisabledToolsV5();

const DEFAULT_DISABLED_TOOLS: ChatToolType[] = [
  ChatToolType.AlphaVantageMcp,
  ChatToolType.AtlasTool,
  ChatToolType.SpawnSubAgentTool,
  ChatToolType.StageMutationsTool,
  ChatToolType.ModifyMutationPlanTool,
];

const loadDisabledTools = (): ChatToolType[] => {
  try {
    const stored = localStorage.getItem(DISABLED_TOOLS_KEY);
    return stored ? JSON.parse(stored) : DEFAULT_DISABLED_TOOLS;
  } catch {
    return DEFAULT_DISABLED_TOOLS;
  }
};

const saveDisabledTools = (tools: ChatToolType[]) => {
  try {
    localStorage.setItem(DISABLED_TOOLS_KEY, JSON.stringify(tools));
  } catch { /* ignore */ }
};

// Not validated here: the model list comes from the server, so a stale key is
// dropped where the list is known (the picker and the chat request).
const loadModel = (): string | null => {
  try {
    return localStorage.getItem(MODEL_KEY);
  } catch { /* ignore */ }
  return null;
};

const saveModel = (model: string) => {
  try {
    localStorage.setItem(MODEL_KEY, model);
  } catch { /* ignore */ }
};

const loadEffort = (): ChatEffort | null => {
  try {
    const stored = localStorage.getItem(EFFORT_KEY);
    if (stored && Object.values(ChatEffort).includes(stored as ChatEffort)) {
      return stored as ChatEffort;
    }
  } catch { /* ignore */ }
  return null;
};

const saveEffort = (effort: ChatEffort) => {
  try {
    localStorage.setItem(EFFORT_KEY, effort);
  } catch { /* ignore */ }
};

const KNOWN_MCP_CATEGORIES = new Set<string>(Object.values(SiriusMcpCategory));

/**
 * Only values the current schema knows survive a load: a category renamed or
 * removed server-side, or a value written by an older build, would otherwise
 * be sent and fail the whole chat request at the schema.
 */
export const loadExcludedMcpCategories = (): SiriusMcpCategory[] => {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(EXCLUDED_MCP_CATEGORIES_KEY) ?? "[]"
    );
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (k): k is SiriusMcpCategory => typeof k === "string" && KNOWN_MCP_CATEGORIES.has(k)
      );
    }
  } catch { /* ignore */ }
  return [];
};

const saveExcludedMcpCategories = (keys: SiriusMcpCategory[]) => {
  try {
    localStorage.setItem(EXCLUDED_MCP_CATEGORIES_KEY, JSON.stringify(keys));
  } catch { /* ignore */ }
};

// =============================================================================
// Slot factories
// =============================================================================

const makeSlot = (sessionId: string = getSessionId()): Slot => ({
  sessionId,
  conversationId: null,
  chatHistory: [],
  userText: "",
  queuedMessage: null,
  shouldStop: false,
  status: "done",   // a brand-new slot has nothing in flight yet
  createdAt: Date.now(),
});

/**
 * Shared helper for all slot mutations. Clones the slots Map and applies
 * `updater` to the entry at `sessionId` if it exists; returns a state patch
 * suitable for `set(state => ...)`. Returns `{}` (no-op) if the slot doesn't
 * exist, so callers don't have to null-check.
 *
 * All slot actions in the store should go through this — the immutable Map
 * clone + `slots.set(sessionId, updater(existing))` pattern was repeated
 * across half a dozen actions before this lived in one place.
 */
function updateSlot(
  state: { slots: Map<string, Slot> },
  sessionId: string,
  updater: (slot: Slot) => Slot
): { slots: Map<string, Slot> } | Record<string, never> {
  const existing = state.slots.get(sessionId);
  if (!existing) return {};
  const slots = new Map(state.slots);
  slots.set(sessionId, updater(existing));
  return { slots };
}

// =============================================================================
// Store
// =============================================================================

const initialSlot = makeSlot();

export const useRangerChatStore = create<ChatState>((set, get) => ({
  slots: new Map([[initialSlot.sessionId, initialSlot]]),
  activeId: initialSlot.sessionId,

  disabledTools: loadDisabledTools(),
  showTools: false,
  menuOpen: false,
  menuActiveTab: "tools",
  knowledgeBaseId: null,
  model: loadModel(),
  effort: loadEffort(),
  excludedMcpCategories: loadExcludedMcpCategories(),
  completedConversations: new Set(),
  dismissedPlanIds: new Set(),
  autoSendMessage: null,
  pendingConversationId: null,

  // === Slot management ===
  newChat: () => {
    const slot = makeSlot();
    set(state => {
      const slots = new Map(state.slots);
      slots.set(slot.sessionId, slot);
      return { slots, activeId: slot.sessionId };
    });
    return slot.sessionId;
  },

  switchToSlot: sessionId =>
    set(state => (state.slots.has(sessionId) ? { activeId: sessionId } : {})),

  switchToConversation: conversationId => {
    for (const slot of get().slots.values()) {
      if (slot.conversationId === conversationId) {
        set({ activeId: slot.sessionId });
        return true;
      }
    }
    return false;
  },

  getSlotByConversationId: conversationId => {
    for (const slot of get().slots.values()) {
      if (slot.conversationId === conversationId) return slot;
    }
    return undefined;
  },

  // === Slot mutations ===
  patchSlot: (sessionId, patch) =>
    set(state => updateSlot(state, sessionId, s => ({ ...s, ...patch }))),

  patchActiveSlot: patch =>
    set(state => updateSlot(state, state.activeId, s => ({ ...s, ...patch }))),

  appendToSlotChatHistory: (sessionId, message) =>
    set(state =>
      updateSlot(state, sessionId, s => ({
        ...s,
        chatHistory: [...s.chatHistory, message],
      }))
    ),

  appendToActiveChatHistory: message =>
    set(state =>
      updateSlot(state, state.activeId, s => ({
        ...s,
        chatHistory: [...s.chatHistory, message],
      }))
    ),

  setSlotChatHistory: (sessionId, messagesOrUpdateFn) =>
    set(state =>
      updateSlot(state, sessionId, s => ({
        ...s,
        chatHistory:
          typeof messagesOrUpdateFn === "function"
            ? messagesOrUpdateFn(s.chatHistory)
            : messagesOrUpdateFn,
      }))
    ),

  setActiveChatHistory: messagesOrUpdateFn =>
    set(state =>
      updateSlot(state, state.activeId, s => ({
        ...s,
        chatHistory:
          typeof messagesOrUpdateFn === "function"
            ? messagesOrUpdateFn(s.chatHistory)
            : messagesOrUpdateFn,
      }))
    ),

  setSlotStatus: (sessionId, status) =>
    set(state => {
      const existing = state.slots.get(sessionId);
      if (!existing) return {};
      const patch = updateSlot(state, sessionId, s => ({ ...s, status }));
      // Side effect: when a slot with a convId transitions to "done", record
      // the convId in the global completedConversations set (drives the green
      // checkmark in the sidebar).
      if (status === "done" && existing.conversationId !== null) {
        const completed = new Set(state.completedConversations);
        completed.add(existing.conversationId);
        return { ...patch, completedConversations: completed };
      }
      return patch;
    }),

  // === Queue (active slot) ===
  appendToQueuedMessage: message =>
    set(state =>
      updateSlot(state, state.activeId, s => ({
        ...s,
        queuedMessage: s.queuedMessage ? `${s.queuedMessage}\n${message}` : message,
      }))
    ),

  consumeQueuedMessage: () => {
    const state = get();
    const msg = state.slots.get(state.activeId)?.queuedMessage ?? null;
    if (msg !== null) {
      set(s => updateSlot(s, s.activeId, slot => ({ ...slot, queuedMessage: null })));
    }
    return msg;
  },

  editQueuedMessage: () => {
    const state = get();
    const msg = state.slots.get(state.activeId)?.queuedMessage ?? null;
    if (msg !== null) {
      set(s => updateSlot(s, s.activeId, slot => ({ ...slot, queuedMessage: null })));
    }
    return msg;
  },

  clearQueuedMessage: () =>
    set(state => {
      const existing = state.slots.get(state.activeId);
      if (!existing || existing.queuedMessage === null) return {};
      return updateSlot(state, state.activeId, s => ({ ...s, queuedMessage: null }));
    }),

  // === Global ===
  setDisabledTools: tools => {
    saveDisabledTools(tools || []);
    set({ disabledTools: tools });
  },
  setShowTools: show => set({ showTools: show }),
  setMenuOpen: open => set({ menuOpen: open }),
  setMenuActiveTab: tab => set({ menuActiveTab: tab }),
  setKnowledgeBaseId: id => set({ knowledgeBaseId: id }),
  setModel: model => {
    saveModel(model);
    set({ model });
  },
  setExcludedMcpCategories: keys => {
    saveExcludedMcpCategories(keys);
    set({ excludedMcpCategories: keys });
  },
  setEffort: effort => {
    saveEffort(effort);
    set({ effort });
  },
  markConversationCompleted: conversationId =>
    set(state => {
      const next = new Set(state.completedConversations);
      next.add(conversationId);
      return { completedConversations: next };
    }),
  dismissPlan: planId =>
    set(state => {
      const dismissed = new Set(state.dismissedPlanIds);
      dismissed.add(planId);
      return { dismissedPlanIds: dismissed };
    }),
  undismissPlan: planId =>
    set(state => {
      const dismissed = new Set(state.dismissedPlanIds);
      dismissed.delete(planId);
      return { dismissedPlanIds: dismissed };
    }),
  setAutoSendMessage: message => set({ autoSendMessage: message }),
  consumeAutoSendMessage: () => {
    const { autoSendMessage } = get();
    set({ autoSendMessage: null });
    return autoSendMessage;
  },
  // Deliberately touches nothing else: the chat panel loads the conversation
  // into the active slot, so there is no menu or tab to open on the way.
  openConversation: conversationId =>
    set({ pendingConversationId: conversationId }),
  consumePendingConversationId: () => {
    const { pendingConversationId } = get();
    set({ pendingConversationId: null });
    return pendingConversationId;
  },
}));

/**
 * The chat request's category exclusions: omitted when there are none. An
 * empty list and an absent one mean the same to the server, and absent keeps
 * the request — and every recording's hash — exactly as it was before the
 * setting existed.
 */
export function mcpExclusionVariables(
  excluded: SiriusMcpCategory[]
): { excludedMcpCategories?: SiriusMcpCategory[] } {
  return excluded.length > 0 ? { excludedMcpCategories: excluded } : {};
}
